/**
 * Generation / preflight failure outcomes (constraint-semantics refactor,
 * surface 4).
 *
 * Unlike the evaluator's stored violation rows (see {@link ./violations}), a
 * generation failure is NOT a row — it arrives as a synchronous 422 detail or
 * the flight's stored `generation_error`. This module is the single, pure place
 * that classifies such a failure into a user-facing *kind* and pulls the
 * "limited by setup" explanation (`blocked_by`) and reduced ceiling out of the
 * payload, so the failure UIs (the review error state, the partial-generation
 * card list, the generate modal) all read it the same way.
 *
 * Invariant 4: a provable `infeasible_configuration` reads "Setup impossible"
 * (the hard rules leave no legal schedule), distinct from a solver that merely
 * ran out of time on a too-aggressive target ("Schedule too tight" — a retry at
 * a lower target can still work). Only the former is a setup limitation; the
 * latter is recoverable. Everything else stays the generic failure.
 */

export type GenerationFailureKind = 'setup_impossible' | 'too_tight' | 'generic'

/**
 * Error codes that prove the configuration itself cannot be scheduled — the
 * usable slots/capacity are exhausted or a hard restriction leaves a matchup
 * with no common slot. These are `infeasible_configuration` setup limitations.
 */
const SETUP_IMPOSSIBLE_CODES = new Set([
  'LOCALLY_IMPOSSIBLE',
  'GLOBALLY_IMPOSSIBLE',
  'CAPACITY_EXCEEDED',
  'NO_VALID_SLOTS',
])

/**
 * Codes where the solver gave up on a too-aggressive target within its time
 * budget — NOT a proof of impossibility. A retry at a lower games-per-team can
 * still succeed, so these are framed as "too tight", never "setup impossible".
 */
const TOO_TIGHT_CODES = new Set(['SOLVER_INFEASIBLE', 'SOLVER_TIMEOUT'])

/**
 * Classify a generation failure from its error code and optional outcome tag.
 *
 * The `outcome` (when the scheduler/api2 image is new enough to send it) is
 * authoritative: `infeasible_configuration` → setup_impossible regardless of
 * code. Otherwise fall back to the code buckets so older payloads still get the
 * right framing. Unknown codes stay `generic`.
 */
export function classifyGenerationFailure(
  code: string | null | undefined,
  outcome?: string | null
): GenerationFailureKind {
  if (
    outcome === 'infeasible_configuration' ||
    outcome === 'setup_impossible'
  ) {
    return 'setup_impossible'
  }
  if (code && SETUP_IMPOSSIBLE_CODES.has(code)) {
    return 'setup_impossible'
  }
  if (code && TOO_TIGHT_CODES.has(code)) {
    return 'too_tight'
  }
  return 'generic'
}

/** Whether a failure is a setup limitation (genuine impossibility proof). */
export function isSetupImpossibleFailure(
  code: string | null | undefined,
  outcome?: string | null
): boolean {
  return classifyGenerationFailure(code, outcome) === 'setup_impossible'
}

/** Headline for the failure screen, by kind. */
export function generationFailureTitle(
  kind: GenerationFailureKind,
  wasCancelled = false
): string {
  if (wasCancelled) {
    return 'Schedule Generation Cancelled'
  }
  if (kind === 'setup_impossible') {
    return 'Setup impossible'
  }
  if (kind === 'too_tight') {
    return 'Schedule too tight'
  }
  return 'Schedule Generation Failed'
}

/**
 * Short badge label for a failed division card, by kind. `null` for the generic
 * kind — callers fall back to the raw error code so nothing is hidden.
 */
export function generationFailureBadge(
  kind: GenerationFailureKind
): string | null {
  if (kind === 'setup_impossible') {
    return 'Setup impossible'
  }
  if (kind === 'too_tight') {
    return 'Too tight'
  }
  return null
}

// ── blocked_by ("Limited by setup") explanation ──────────────────────────────

export interface GenerationBlocker {
  constraintType: string
  teamUids: string[]
  divisionUids: string[]
}

/**
 * Friendly, lower-case noun phrase for a blocker constraint type, for use mid
 * sentence ("limited by a time window and rest-day rules"). Falls back to a
 * de-snaked form for any unmapped/new type.
 */
export function humanizeBlockerType(constraintType: string): string {
  switch (constraintType) {
    case 'time_window':
      return 'a time window'
    case 'preferred_time':
      return 'a required time-of-day'
    case 'preferred_day':
      return 'a required day-of-week'
    case 'restricted_day':
      return 'a blocked day-of-week'
    case 'team_bye_period':
      return 'a team bye period'
    case 'venue_blackout':
      return 'a venue blackout'
    case 'venue_capacity':
      return 'venue capacity'
    case 'min_rest_days':
      return 'minimum rest days'
    case 'no_back_to_back':
      return 'a no-back-to-back rule'
    case 'max_games_per_team_per_day':
      return 'a per-day game cap'
    case 'games_per_team_per_week':
      return 'a weekly game cap'
    default:
      return constraintType.split('_').filter(Boolean).join(' ')
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null

/** Read one raw `blocked_by` entry into the normalized shape; null if invalid. */
function readBlocker(raw: unknown): GenerationBlocker | null {
  if (!isRecord(raw)) {
    return null
  }
  const constraintType = raw.constraint_type
  if (typeof constraintType !== 'string' || constraintType.length === 0) {
    return null
  }
  const teamUids = Array.isArray(raw.team_uids)
    ? raw.team_uids.filter((t): t is string => typeof t === 'string')
    : []
  const divisionUids = Array.isArray(raw.division_uids)
    ? raw.division_uids.filter((d): d is string => typeof d === 'string')
    : []
  return { constraintType, teamUids, divisionUids }
}

/**
 * Pull the `blocked_by` blockers out of a generation error payload — either the
 * synchronous 422 detail or the flight's stored `generation_error`. Both carry
 * a top-level `blocked_by` (deduped across divisions); when absent, fall back to
 * unioning the per-division `divisions[].blocked_by`. Deduped by constraint type.
 * Null-safe: unknown/old payloads yield `[]`.
 */
export function readBlockedBy(error: unknown): GenerationBlocker[] {
  if (!isRecord(error)) {
    return []
  }
  const raw: unknown[] = []
  if (Array.isArray(error.blocked_by)) {
    raw.push(...error.blocked_by)
  } else if (Array.isArray(error.divisions)) {
    for (const div of error.divisions) {
      if (isRecord(div) && Array.isArray(div.blocked_by)) {
        raw.push(...div.blocked_by)
      }
    }
  }
  const seen = new Set<string>()
  const out: GenerationBlocker[] = []
  for (const entry of raw) {
    const blocker = readBlocker(entry)
    if (!blocker || seen.has(blocker.constraintType)) {
      continue
    }
    seen.add(blocker.constraintType)
    out.push(blocker)
  }
  return out
}

/**
 * One-line "Limited by …" explanation from a blocker list, or null when there
 * is nothing to explain. Joins the friendly type phrases naturally
 * ("a time window and minimum rest days").
 */
export function summarizeBlockers(
  blockers: GenerationBlocker[]
): string | null {
  if (blockers.length === 0) {
    return null
  }
  const phrases = blockers.map((b) => humanizeBlockerType(b.constraintType))
  let joined: string
  if (phrases.length === 1) {
    joined = phrases[0]
  } else if (phrases.length === 2) {
    joined = `${phrases[0]} and ${phrases[1]}`
  } else {
    joined = `${phrases.slice(0, -1).join(', ')}, and ${
      phrases[phrases.length - 1]
    }`
  }
  return `Limited by ${joined}.`
}

// ============================================================================
// Config-error affordance routing (D4, 2026-07 audit)
// ============================================================================

/**
 * Codes whose fix lives on the Time Slots tab: capacity/slot shortfalls,
 * duration/window mismatches, and rest/weekly windows that need more
 * play-dates.
 */
const TIME_SLOT_CONFIG_CODES = new Set([
  'LOCALLY_IMPOSSIBLE',
  'GLOBALLY_IMPOSSIBLE',
  'CAPACITY_EXCEEDED',
  'NO_VALID_SLOTS',
  'GAME_DURATION_EXCEEDS_SLOTS',
  'UNKNOWN_FACILITY',
  'REST_CONSTRAINT_INFEASIBLE',
  'WEEKLY_REST_INFEASIBLE',
])

/** Codes whose fix lives on the Divisions & Teams tab. */
const DIVISION_CONFIG_CODES = new Set(['NO_MATCHUPS', 'TOO_MANY_TEAMS'])

/**
 * Codes that are NOT configuration problems — transient/service failures or a
 * too-aggressive target where a plain retry (or apply-and-retry) is the right
 * affordance. A structured code here must show "Retry Generation" even when
 * the human-readable message happens to mention "venue" or "division".
 */
const NON_CONFIG_RETRYABLE_CODES = new Set([
  'SOLVER_TIMEOUT',
  'SOLVER_INFEASIBLE',
  'SOLVER_ERROR',
  'DISPATCH_FAILED',
  'PARTITION_FAILED',
  'CANCELLED',
])

export interface ConfigErrorAffordance {
  /** Show a "Go to <tab>" fix-config affordance instead of plain retry. */
  isConfigError: boolean
  /** The fix-config tab: true → Time Slots, false → Divisions & Teams. */
  needsTimeSlots: boolean
}

/**
 * Route the failure screen's affordance (fix-config tab vs Retry Generation)
 * from the STRUCTURED error code first, falling back to the legacy
 * message-string match only when no code is available (older payloads).
 *
 * Pre-fix the routing was string-match ONLY: any error message that happened
 * to contain "venue" / "capacity" / "division" (e.g. a DISPATCH_FAILED whose
 * detail echoed a division name) suppressed the retry affordance on a
 * perfectly retryable failure.
 */
export function classifyConfigErrorAffordance(
  code: string | null | undefined,
  message: string
): ConfigErrorAffordance {
  if (code && TIME_SLOT_CONFIG_CODES.has(code)) {
    return { isConfigError: true, needsTimeSlots: true }
  }
  if (code && DIVISION_CONFIG_CODES.has(code)) {
    return { isConfigError: true, needsTimeSlots: false }
  }
  if (code && NON_CONFIG_RETRYABLE_CODES.has(code)) {
    return { isConfigError: false, needsTimeSlots: false }
  }
  // Unknown/absent code → legacy string-match fallback.
  const lowerMsg = message.toLowerCase()
  const needsTimeSlots =
    lowerMsg.includes('time slot') ||
    lowerMsg.includes('venue') ||
    lowerMsg.includes('capacity')
  const isConfigError =
    needsTimeSlots || lowerMsg.includes('division') || lowerMsg.includes('team')
  return { isConfigError, needsTimeSlots }
}
